import { useLayoutEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Paperclip,
  PenTool,
  Redo2,
  RotateCcw,
  Trash2,
  Undo2,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/** The draft paragraph: plain text runs and AI suggestions, in reading order. */
const PARAGRAPH: (string | Suggestion)[] = [
  {
    id: 0,
    del: 'Yes, this all sounds great, and thank you so much for planning everything.',
    ins: 'Sounds good.',
  },
  ' Friday at 2 works',
  {
    id: 1,
    del: ', I think, although I might need to check whether I can leave a little early',
    ins: ', and I’ll bring firewood',
  },
  '.',
  { id: 2, ins: ' I’ll check the road is open past Gorda.' },
];

const SUGGESTION_IDS = [0, 1, 2];

const INITIAL_ACTIVE = 2;

/** Approximate rendered width of the Reject/Accept popover, to keep it inside the editor. */
const POPOVER_WIDTH = 168;

const INITIAL_HISTORY: Entry[] = [
  { decisions: SUGGESTION_IDS.map(() => 'pending'), active: INITIAL_ACTIVE },
];

const OUTLINE_BUTTON =
  'border-gray-7 text-gray-11 hover:bg-gray-3 hover:text-gray-12 inline-flex h-8 shrink-0 items-center justify-center border text-sm font-medium transition-colors disabled:pointer-events-none [&_svg]:size-4';

const ICON_BUTTON =
  'text-gray-11 hover:bg-gray-3 hover:text-gray-12 flex size-8 items-center justify-center rounded-md transition-colors [&_svg]:size-4';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type Suggestion = { id: number; del?: string; ins?: string };

type Decision = 'pending' | 'accepted' | 'rejected';

/** One step of review history; `active` is the suggestion in focus at that step. */
type Entry = { decisions: Decision[]; active: number | null };

type AiEditorMockupProps = { className?: string };

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/**
 * A static copy of the composer reviewing AI suggestions. Accept, reject, step through,
 * undo and redo all work on local state so visitors can try the review flow.
 */
const AiEditorMockup: React.FC<AiEditorMockupProps> = ({ className }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const attachmentInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const [ccOpen, setCcOpen] = useState<boolean>(false);
  const [cc, setCc] = useState<string>('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [editedText, setEditedText] = useState<string | null>(null);
  const [status, setStatus] = useState<'sent' | 'discarded' | null>(null);
  const [history, setHistory] = useState<Entry[]>(INITIAL_HISTORY);
  const [index, setIndex] = useState<number>(0);
  const [active, setActive] = useState<number | null>(INITIAL_ACTIVE);
  const [popover, setPopover] = useState<{ left: number; top: number } | null>(null);

  const { decisions } = history[index];
  const pending = SUGGESTION_IDS.filter((id) => decisions[id] === 'pending');
  const reviewing = pending.length > 0;
  const position = active === null ? -1 : pending.indexOf(active);

  // Place the Reject/Accept popover in the space under the paragraph, lined up with the last
  // line of the active suggestion so it never covers text. Rects are divided by the rendered
  // scale because the landing page shrinks mockups with a transform.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (body?.closest('[inert]')) return;
    if (!body || active === null) {
      setPopover(null);
      return;
    }
    const measure = () => {
      const target = body.querySelector(`[data-suggestion="${active}"]`);
      const rects = target?.getClientRects();
      const paragraph = target?.parentElement;
      if (!rects?.length || !paragraph) return setPopover(null);
      const box = body.getBoundingClientRect();
      const scale = box.width / body.offsetWidth || 1;
      const left = (rects[rects.length - 1].left - box.left) / scale;
      setPopover({
        left: Math.min(left, body.offsetWidth - POPOVER_WIDTH - 16),
        top: (paragraph.getBoundingClientRect().bottom - box.top) / scale + 16,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [active, decisions]);

  const commit = (next: Decision[]) => {
    const nextPending = SUGGESTION_IDS.filter((id) => next[id] === 'pending');
    const nextActive = nextPending.find((id) => id > (active ?? -1)) ?? nextPending[0] ?? null;
    setHistory([
      ...history.slice(0, index),
      { decisions, active },
      { decisions: next, active: nextActive },
    ]);
    setIndex(index + 1);
    setActive(nextActive);
  };

  const resolve = (id: number, decision: Decision) =>
    commit(decisions.map((value, i) => (i === id ? decision : value)));

  const resolveAll = (decision: Decision) =>
    commit(decisions.map((value) => (value === 'pending' ? decision : value)));

  const travel = (to: number) => {
    setIndex(to);
    setActive(history[to].active);
  };

  const step = (direction: 1 | -1) => {
    if (!reviewing) return;
    setActive(pending[(Math.max(position, 0) + direction + pending.length) % pending.length]);
  };

  const reset = () => {
    setHistory(INITIAL_HISTORY);
    setIndex(0);
    setActive(INITIAL_ACTIVE);
    setEditedText(null);
    setStatus(null);
  };

  if (status)
    return (
      <div className="bg-gray-2 border-gray-6 text-gray-12 flex h-[400px] w-[568px] flex-col items-center justify-center gap-4 rounded-lg border">
        <p role="status">{status === 'sent' ? 'Message sent in this demo' : 'Draft discarded'}</p>
        <button
          type="button"
          className="bg-gray-12 text-gray-1 rounded-md px-3 py-2 text-sm"
          onClick={() => {
            reset();
            setAttachments([]);
            setCc('');
            setCcOpen(false);
          }}
        >
          New draft
        </button>
      </div>
    );

  return (
    <div
      aria-label="AI editor demo"
      onKeyDown={(event) => {
        if (!(event.metaKey || event.ctrlKey)) return;
        const key = event.key.toLowerCase();
        if (reviewing && key === 'y' && active !== null) {
          event.preventDefault();
          resolve(active, 'accepted');
        } else if (reviewing && key === 'n' && active !== null) {
          event.preventDefault();
          resolve(active, 'rejected');
        } else if (reviewing && key === 'enter') {
          event.preventDefault();
          resolveAll('accepted');
        } else if (reviewing && key === 'backspace') {
          event.preventDefault();
          resolveAll('rejected');
        } else if (key === 'z' && !editedText) {
          event.preventDefault();
          const next = index + (event.shiftKey ? 1 : -1);
          if (next >= 0 && next < history.length) travel(next);
        }
      }}
      className={clsx(
        'border-gray-6 bg-gray-2 text-gray-12 relative w-[568px] overflow-hidden rounded-lg border font-sans antialiased',
        className
      )}
    >
      <div className="text-gray-11 flex h-10 items-center gap-2 px-4 text-sm">
        to <b className="text-gray-12 font-medium">Ana Ruiz</b>
        <button
          type="button"
          className="hover:text-gray-12 ml-auto transition-colors"
          onClick={() => setCcOpen(!ccOpen)}
          aria-expanded={ccOpen}
        >
          Cc
        </button>
      </div>
      {ccOpen ? (
        <label className="border-gray-5 flex gap-2 border-t px-4 py-2 text-sm">
          Cc
          <input
            type="email"
            aria-label="Cc recipient"
            className="min-w-0 flex-1 bg-transparent outline-none"
            value={cc}
            onChange={(event) => setCc(event.target.value)}
          />
        </label>
      ) : null}
      <div
        ref={bodyRef}
        className="border-gray-5 relative min-h-[288px] border-t px-4 pt-3 pb-16 text-base leading-6"
      >
        {!reviewing ? (
          <textarea
            aria-label="Draft message"
            className="h-[218px] w-full resize-none bg-transparent outline-none"
            value={
              editedText ??
              `Hi Ana,\n\n${PARAGRAPH.map((part) => (typeof part === 'string' ? part : decisions[part.id] === 'accepted' ? (part.ins ?? '') : (part.del ?? ''))).join('')}\n\nSam`
            }
            onChange={(event) => setEditedText(event.target.value)}
          />
        ) : (
          <>
            <p className="mb-4">Hi Ana,</p>
            <p className={reviewing ? 'mb-[52px]' : 'mb-4'}>
              {PARAGRAPH.map((part) => {
                if (typeof part === 'string') return part;
                const decision = decisions[part.id];
                if (decision === 'accepted') return part.ins;
                if (decision === 'rejected') return part.del;
                const on = part.id === active;
                return (
                  <span
                    key={part.id}
                    data-suggestion={part.id}
                    className={clsx(
                      'cursor-pointer rounded-[4px] border transition-colors',
                      on ? 'border-blue-9' : 'hover:border-blue-7 border-transparent'
                    )}
                    onClick={() => setActive(part.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Review suggestion ${part.id + 1}`}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setActive(part.id);
                      }
                    }}
                  >
                    {part.del ? (
                      <del
                        className={clsx(
                          'text-red-11 decoration-red-9 rounded-[4px] px-0.5 line-through',
                          on ? 'bg-red-4' : 'bg-red-3'
                        )}
                      >
                        {part.del}
                      </del>
                    ) : null}
                    {part.ins ? (
                      <ins
                        className={clsx(
                          'text-green-11 rounded-[4px] px-0.5 no-underline',
                          on ? 'bg-green-4' : 'bg-green-3'
                        )}
                      >
                        {part.ins}
                      </ins>
                    ) : null}
                  </span>
                );
              })}
            </p>
            <p>Sam</p>
          </>
        )}
        {reviewing && active !== null && popover ? (
          <div
            className="bg-gray-2/60 absolute flex rounded-md shadow-lg backdrop-blur-[8px]"
            style={popover}
          >
            <button
              type="button"
              className="border-gray-7 text-gray-11 hover:bg-gray-3 hover:text-gray-12 inline-flex h-6 items-center gap-1.5 rounded-l-md border px-2 text-xs leading-4 font-medium transition-colors"
              onClick={() => resolve(active, 'rejected')}
            >
              Reject <Kbd small>⌘N</Kbd>
            </button>
            <button
              type="button"
              className="border-gray-7 text-gray-11 hover:bg-gray-3 hover:text-gray-12 -ml-px inline-flex h-6 items-center gap-1.5 rounded-r-md border px-2 text-xs leading-4 font-medium transition-colors"
              onClick={() => resolve(active, 'accepted')}
            >
              Accept <Kbd small>⌘Y</Kbd>
            </button>
          </div>
        ) : null}
      </div>
      {reviewing ? (
        <div className="border-gray-6 bg-gray-2/80 absolute inset-x-2 bottom-14 flex h-10 items-center gap-1 rounded-lg border px-1 shadow-md backdrop-blur-[8px]">
          <div className="flex">
            <button
              type="button"
              className={clsx(OUTLINE_BUTTON, 'w-8 rounded-l-md')}
              aria-label="Previous suggestion"
              onClick={() => step(-1)}
            >
              <ChevronLeft />
            </button>
            <span className="border-gray-7 text-gray-11 -ml-px flex h-8 items-center border px-1.5 text-base tabular-nums">
              {position + 1}/{pending.length}
            </span>
            <button
              type="button"
              className={clsx(OUTLINE_BUTTON, '-ml-px w-8 rounded-r-md')}
              aria-label="Next suggestion"
              onClick={() => step(1)}
            >
              <ChevronRight />
            </button>
          </div>
          <div className="ml-1 flex">
            <button
              type="button"
              disabled={index === 0}
              className={clsx(OUTLINE_BUTTON, 'w-8 rounded-l-md')}
              aria-label="Undo review decision"
              onClick={() => travel(index - 1)}
            >
              <Undo2 />
            </button>
            <button
              type="button"
              disabled={index === history.length - 1}
              className={clsx(OUTLINE_BUTTON, '-ml-px w-8 rounded-r-md')}
              aria-label="Redo review decision"
              onClick={() => travel(index + 1)}
            >
              <Redo2 />
            </button>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            className={clsx(OUTLINE_BUTTON, 'gap-1.5 rounded-md px-3')}
            onClick={() => resolveAll('rejected')}
          >
            Reject all <Kbd>⌘⌫</Kbd>
          </button>
          <button
            type="button"
            className={clsx(
              OUTLINE_BUTTON,
              'bg-gray-4 text-gray-12 hover:bg-gray-5 gap-1.5 rounded-md px-3'
            )}
            onClick={() => resolveAll('accepted')}
          >
            Accept all <Kbd>⌘↵</Kbd>
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="text-gray-11 hover:bg-gray-3 hover:text-gray-12 absolute bottom-[62px] left-2 flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors"
          onClick={reset}
        >
          <RotateCcw className="size-3.5" />
          Reset suggestions
        </button>
      )}
      {attachments.length ? (
        <div className="flex flex-wrap gap-2 px-3 pb-2">
          {attachments.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className="border-gray-6 flex items-center gap-2 rounded border px-2 py-1 text-xs"
            >
              {name}
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => setAttachments(attachments.filter((_, at) => at !== index))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <input
        ref={attachmentInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          setAttachments([
            ...attachments,
            ...Array.from(event.target.files ?? []).map((file) => file.name),
          ]);
          event.target.value = '';
        }}
      />
      <input
        ref={imageInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          setAttachments([
            ...attachments,
            ...Array.from(event.target.files ?? []).map((file) => file.name),
          ]);
          event.target.value = '';
        }}
      />
      <div className="border-gray-6 flex items-center gap-1 border-t p-2">
        <button
          type="button"
          className="text-gray-12 hover:bg-gray-3 flex h-8 items-center rounded-md px-3 text-sm font-medium transition-colors disabled:opacity-50"
          disabled={reviewing}
          title={reviewing ? 'Resolve the suggestions before sending' : 'Send message'}
          onClick={() => setStatus('sent')}
        >
          Send
        </button>
        <button
          type="button"
          className={clsx(ICON_BUTTON, reviewing && 'bg-gray-4 text-gray-12 hover:bg-gray-5')}
          aria-label="Review AI suggestions"
          onClick={reset}
        >
          <PenTool />
        </button>
        <button
          type="button"
          aria-label="Attach files"
          onClick={() => attachmentInput.current?.click()}
          className={ICON_BUTTON}
        >
          <Paperclip />
        </button>
        <button
          type="button"
          aria-label="Insert images"
          onClick={() => imageInput.current?.click()}
          className={ICON_BUTTON}
        >
          <ImageIcon />
        </button>
        <div className="flex-1" />
        <button
          type="button"
          aria-label="Discard draft"
          onClick={() => setStatus('discarded')}
          className={ICON_BUTTON}
        >
          <Trash2 />
        </button>
      </div>
    </div>
  );
};

const Kbd: React.FC<{ small?: boolean; children: React.ReactNode }> = ({ small, children }) => (
  <span
    className={clsx(
      'border-gray-6 bg-gray-3 text-gray-12 inline-flex items-center justify-center border leading-none font-normal',
      small
        ? 'h-4 min-w-4 rounded-[4px] px-[3px] text-[10px]'
        : 'h-5 min-w-5 rounded-md px-[5px] text-xs'
    )}
  >
    {children}
  </span>
);

export default AiEditorMockup;
